/**
 * @file コントロール設定
 * @namespace rahmen
 */

/**
 * @module controls
 */

import validators from '@/rahmen/validators'
/**
 * 入力制御種類
 * @enum
 */
const InputTypes = {
  /**
   * 数字タイプ
   * @type Number
   */
  Number: 0,
  /**
   * 半角英数タイプ
   * @type Number
   */
  Alphanumeric: 1,
  /**
   * 全角タイプ
   * @type Number
   */
  FullAngle: 2,
  /**
   * 全角カタカナタイプ
   * @type Number
   */
  FullWidthKana: 3,
  /**
   * 半角カナカナタイプ
   * @type Number
   */
  HalfWidthKana: 4,
  /**
   * ひらがなタイプ
   * @type Number
   */
  Hiragana: 5,
  /**
   * 日付タイプ
   * @type Number
   */
  Date: 6,
  /**
   * 数値タイプ
   * @type Number
   */
  Numerical: 7,
  /**
   * 半角英数+記号タイプ
   * @type Number
   */
  AlphanumericWithSymbol: 8,
  /**
   * 時刻タイプ
   * @type Number
   */
  Time: 9,
  /**
   * 年月タイプ
   * @type Number
   */
  YearMonth: 10,
  /**
   * 半角タイプ
   * @type Number
   */
  HalfAngle: 11,
  /**
   * 項目長一致 数字タイプ
   * @type Number
   */
  FixedLengthNumber: 12,
  /**
   * 項目長一致 半角英数タイプ
   * @type Number
   */
  FixedLengthAlphanumeric: 13,
  /**
   * 項目長一致 全角タイプ
   * @type Number
   */
  FixedLengthFullAngle: 14,
  /**
   * 項目長一致 全角カタカナタイプ
   * @type Number
   */
  FixedLengthFullWidthKana: 15,
  /**
   * 項目長一致 半角カナカナタイプ
   * @type Number
   */
  FixedLengthHalfWidthKana: 16,
  /**
   * 項目長一致 ひらがなタイプ
   * @type Number
   */
  FixedLengthHiragana: 17,
  /**
   * 項目長一致 数値タイプ
   * @type Number
   */
  FixedLengthNumerical: 18,
  /**
   * 項目長一致 半角英数+記号タイプ
   * @type Number
   */
  FixedLengthAlphanumericWithSymbol: 19,
  /**
   * 項目長一致 半角タイプ
   * @type Number
   */
  FixedLengthHalfAngle: 20,
  /**
   * 時間タイプ
   * @type Number
   */
  TimeSpan: 21,
  /**
   * 項目長一致 制御無し
   * @type Number
   */
  FixedLengthFree: 98,
  /**
   * 制御無し
   * @type Number
   */
  Free: 99
}

/**
 * 検証メソッド定義名
 * @member
 */
const VALIDATE_METHOD = 'InvokeValidate'

/**
 * フォーマットメソッド定義名
 * @member
 */
const FORMAT_METHOD = 'format'

/**
 * エラーメッセージ設定メソッド定義名
 * @member
 */
const SET_INVALID_METHOD = 'setInvalidData'

/**
 * エラーメッセージ取得メソッド定義名
 * @member
 */
const GET_INVALID_METHOD = 'getInvalidData'

/**
 * エラーメッセージクリアメソッド定義名
 * @member
 */
const CLEAR_INVALID_METHOD = 'clearInvalidData'

/**
 * コントロール色指定
 * @enum
 * @see input内の文字色は、propsでの上書きは不可の為、各コンポーネントに記載する
 */
const Colors = {
  /**
   * 必須背景色
   * @type String
   */
  Required: '#FFE6EF',
  /**
   * タイトル前景色
   * @type String
   */
  Title: 'Black',
  /**
   * 標準ボタン背景色
   * @type String
   */
  ButtonDefaultBackground: 'blue',
  /**
   * フッターボタン背景色
   * @type String
   */
  ButtonFooterBackground: '#2F75B5',
  /**
   * ヘッダ背景色
   * @type String
   */
  HeaderBackground: '#2F75B5',
  /**
   * フッターボタンフォント色
   * @type String
   */
  ButtonFooterFont: 'white',
  /**
   * ヘッダーフォント色
   * @type String
   */
  HeaderFont: 'white'
}

/**
 * ドロップダウンの値項目プロパティ名指定
 */
const SelectValuePropName = 'value'

/**
 * ドロップダウンの表示項目プロパティ名指定
 */
const SelectTextPropName = 'text'

/**
 * 入力制御設定
 */
const InputTypeDetails = [
  {
    id: InputTypes.Number,
    placeholder: '数字@@length@@文字以内',
    validators: [
      {
        seq: 10,
        method: validators.numberValidate
      },
      {
        seq: 20,
        method: validators.lengthValidate
      }
    ]
  },
  {
    id: InputTypes.Alphanumeric,
    placeholder: '半角英数字@@length@@文字以内',
    validators: [
      {
        seq: 10,
        method: validators.alphanumericValidate
      },
      {
        seq: 20,
        method: validators.lengthValidate
      }
    ]
  },
  {
    id: InputTypes.AlphanumericWithSymbol,
    placeholder: '半角英数字@@length@@文字以内',
    validators: [
      {
        seq: 10,
        method: validators.alphanumericWithSymbolValidate
      },
      {
        seq: 20,
        method: validators.lengthValidate
      }
    ]
  },
  {
    id: InputTypes.FullAngle,
    placeholder: '全角@@length@@文字以内',
    validators: [
      {
        seq: 10,
        method: validators.fullAngleValidate
      },
      {
        seq: 20,
        method: validators.lengthValidate
      }
    ]
  },
  {
    id: InputTypes.HalfAngle,
    placeholder: '半角@@length@@文字以内',
    validators: [
      {
        seq: 10,
        method: validators.halfAngleValidate
      },
      {
        seq: 20,
        method: validators.lengthValidate
      }
    ]
  },
  {
    id: InputTypes.FullWidthKana,
    placeholder: '全角カナ@@length@@文字以内',
    validators: [
      {
        seq: 10,
        method: validators.fullWidthKanaValidate
      },
      {
        seq: 20,
        method: validators.lengthValidate
      }
    ]
  },
  {
    id: InputTypes.HalfWidthKana,
    placeholder: '半角カナ@@length@@文字以内',
    validators: [
      {
        seq: 10,
        method: validators.halfWidthKanaValidate
      },
      {
        seq: 20,
        method: validators.lengthValidate
      }
    ]
  },
  {
    id: InputTypes.Hiragana,
    placeholder: 'ひらがな@@length@@文字以内',
    validators: [
      {
        seq: 10,
        method: validators.hiraganaValidate
      },
      {
        seq: 20,
        method: validators.lengthValidate
      }
    ]
  },
  {
    id: InputTypes.Date,
    placeholder: 'YYYY/MM/DD',
    validators: [
      {
        seq: 10,
        method: validators.dateValidate
      },
      {
        seq: 20,
        method: validators.dateRangeValidate
      }
    ]
  },
  {
    id: InputTypes.Numerical,
    placeholder: '数値@@length@@桁以内',
    validators: [
      {
        seq: 10,
        method: validators.numericalValidate
      },
      {
        seq: 20,
        method: validators.numericalDigitValidate
      },
      {
        seq: 30,
        method: validators.numericalRangeValidate
      }
    ]
  },
  {
    id: InputTypes.Time,
    placeholder: 'HH:MM',
    validators: [
      {
        seq: 10,
        method: validators.timeValidate
      }
    ]
  },
  {
    id: InputTypes.TimeSpan,
    placeholder: 'HHH:MM',
    validators: [
      {
        seq: 10,
        method: validators.timeSpanValidate
      }
    ]
  },
  {
    id: InputTypes.YearMonth,
    placeholder: 'YYYY/MM',
    validators: [
      {
        seq: 10,
        method: validators.yearMonthValidate
      },
      {
        seq: 20,
        method: validators.yearMonthRangeValidate
      }
    ]
  },
  {
    id: InputTypes.FixedLengthNumber,
    placeholder: '数字@@length@@文字で入力',
    validators: [
      {
        seq: 10,
        method: validators.numberValidate
      },
      {
        seq: 20,
        method: validators.fixedLengthValidate
      }
    ]
  },
  {
    id: InputTypes.FixedLengthAlphanumeric,
    placeholder: '半角英数字@@length@@文字で入力',
    validators: [
      {
        seq: 10,
        method: validators.alphanumericValidate
      },
      {
        seq: 20,
        method: validators.fixedLengthValidate
      }
    ]
  },
  {
    id: InputTypes.FixedLengthAlphanumericWithSymbol,
    placeholder: '半角英数字@@length@@文字で入力',
    validators: [
      {
        seq: 10,
        method: validators.alphanumericWithSymbolValidate
      },
      {
        seq: 20,
        method: validators.fixedLengthValidate
      }
    ]
  },
  {
    id: InputTypes.FixedLengthFullAngle,
    placeholder: '全角@@length@@文字で入力',
    validators: [
      {
        seq: 10,
        method: validators.fullAngleValidate
      },
      {
        seq: 20,
        method: validators.fixedLengthValidate
      }
    ]
  },
  {
    id: InputTypes.FixedLengthHalfAngle,
    placeholder: '半角@@length@@文字で入力',
    validators: [
      {
        seq: 10,
        method: validators.halfAngleValidate
      },
      {
        seq: 20,
        method: validators.fixedLengthValidate
      }
    ]
  },
  {
    id: InputTypes.FixedLengthFullWidthKana,
    placeholder: '全角カナ@@length@@文字で入力',
    validators: [
      {
        seq: 10,
        method: validators.fullWidthKanaValidate
      },
      {
        seq: 20,
        method: validators.fixedLengthValidate
      }
    ]
  },
  {
    id: InputTypes.FixedLengthHalfWidthKana,
    placeholder: '半角カナ@@length@@文字で入力',
    validators: [
      {
        seq: 10,
        method: validators.halfWidthKanaValidate
      },
      {
        seq: 20,
        method: validators.fixedLengthValidate
      }
    ]
  },
  {
    id: InputTypes.FixedLengthHiragana,
    placeholder: 'ひらがな@@length@@文字で入力',
    validators: [
      {
        seq: 10,
        method: validators.hiraganaValidate
      },
      {
        seq: 20,
        method: validators.fixedLengthValidate
      }
    ]
  },
  {
    id: InputTypes.FixedLengthNumerical,
    placeholder: '数値@@length@@桁で入力',
    validators: [
      {
        seq: 10,
        method: validators.numericalValidate
      },
      {
        seq: 20,
        method: validators.fixedNumericalDigitValidate
      },
      {
        seq: 30,
        method: validators.numericalRangeValidate
      }
    ]
  },
  {
    id: InputTypes.FixedLengthFree,
    placeholder: '@@length@@文字で入力',
    validators: [
      {
        seq: 10,
        method: validators.fixedLengthValidate
      }
    ]
  },
  {
    id: InputTypes.Free,
    placeholder: '@@length@@文字以内',
    validators: [
      {
        seq: 10,
        method: validators.lengthValidate
      }
    ]
  }
]

/**
 * 指定入力タイプの設定値を取得
 * @param {InputTypes} type 入力タイプ
 * @returns {Object} Object
 * @returns {InputTypes} Object.id 入力タイプ
 * @returns {String}} Object.placeholder 表示プレースホルダ
 * @returns {Array} Object.validators Validator配列
 */
function getInputTypeDetail (type) {
  if (type === undefined) { return {} }
  return InputTypeDetails.find((elem) => {
    return elem.id === type
  })
}

/**
 * 指定入力タイプのValidatorListを取得
 * @param {InputTypes} type 入力タイプ
 * @returns {Array} Object.validators Validator配列
 */
function getInputTypeValidators (type) {
  const d = getInputTypeDetail(type)
  console.log('getInputTypeValidators', d, type)
  if (d === undefined || d.validators === undefined) { return [] }
  return d.validators.sort((a, b) => {
    if (a.seq < b.seq) { return -1 }
    if (a.seq > b.seq) { return 1 }
    return 0
  }).map((x) => {
    return x.method
  })
}

export default {
  InputTypes,
  Colors,
  VALIDATE_METHOD,
  FORMAT_METHOD,
  SET_INVALID_METHOD,
  GET_INVALID_METHOD,
  CLEAR_INVALID_METHOD,
  SelectValuePropName,
  SelectTextPropName,
  getInputTypeDetail,
  getInputTypeValidators
}
